// Home landing (July-14 FR-P1/FR-P2): role-appropriate dashboard inside the existing nav shell.
// Admin → admin-dashboard; Central → central-dashboard; AE/Manager → ae-dashboard;
// Student / QC-in-training → student-dashboard. Portal strip stays at the bottom.
import { Link } from "react-router-dom";
import { type Action } from "@rrid/shared-types";
import { useCan } from "../components/Can.js";
import { useAuth } from "../lib/auth.js";
import { Card, CardBody } from "../components/ui.js";
import { AeDashboardView } from "./home/AeDashboard.js";
import { StudentDashboardView } from "./home/StudentDashboard.js";
import { StaffDashboardView } from "./home/StaffDashboard.js";
import { ResourceHub } from "./home/ResourceHub.js";
import { useProductEvent } from "../lib/telemetry.js";

const PORTALS: { to: string; title: string; emoji: string; action?: Action }[] = [
  // Match top-nav short labels (no “Portal”; pluralize the first two).
  { to: "/preprints", title: "Preprints", emoji: "📄" },
  { to: "/reviewers", title: "Reviewer Proposals", emoji: "🔎" },
  { to: "/quality-check", title: "Quality Check", emoji: "✅" },
  { to: "/operations", title: "Operations", emoji: "📣", action: "openOperations" },
];

export function Home(): JSX.Element {
  useProductEvent("ui.home_opened");
  const { user } = useAuth();
  const can = useCan();
  const roles = user?.roles ?? [];
  const isAdmin = roles.includes("Admin");
  const isCentral = roles.includes("Central");
  // AE-and-above is exactly `advanceToPitchEditorial` with no domain scope (students & QC-in-training
  // are denied it) — the same tell the AE Details page uses.
  const isAe = can("advanceToPitchEditorial", {});
  const portals = PORTALS.filter((p) => !p.action || can(p.action));

  // Both homepage designs are complete pages, ending in the team calendar and the public-site
  // link; portals live in the top nav, so the "Jump to" strip would only duplicate them. Only the
  // Central/Admin staff dashboards, which have no design of their own yet, still carry it.
  let dashboard: JSX.Element;
  let designedHome = true;
  if (isAdmin) {
    dashboard = <StaffDashboardView variant="Admin" />;
    designedHome = false;
  } else if (isCentral) {
    dashboard = <StaffDashboardView variant="Central" />;
    designedHome = false;
  } else if (isAe) dashboard = <AeDashboardView />;
  else dashboard = <StudentDashboardView />;

  return (
    <div className="space-y-6">
      {dashboard}

      {/* Aug 19 sitting / #37: the SoftR hub links every role expects on the landing page. */}
      <ResourceHub />

      {!designedHome && (
        <section>
          <h3 className="mb-2 text-sm font-semibold text-slate-500">Jump to</h3>
          <div className="flex flex-wrap gap-3">
            {portals.map((p) => (
              <Link key={p.to} to={p.to}>
                <Card className="transition hover:border-brand hover:shadow-sm">
                  <CardBody className="flex items-center gap-2 py-2">
                    <span className="text-lg">{p.emoji}</span>
                    <span className="text-sm font-medium text-brand">{p.title}</span>
                  </CardBody>
                </Card>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
